import { useLottie } from "lottie-react";
import { useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { ICONSAX_SIZE } from "../../components/Iconsax/Iconsax.types.js";
import { UseAnimatedIconOptions } from "./types.js";

const TARGET_SCALE = 95;

const ColorInheritWrapper = styled.div`
    svg path[fill] {
        fill: currentColor;
    }
    svg path[stroke] {
        stroke: currentColor;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeLottieIcon = (lottie: any, targetSize: number) => {
    if (!lottie?.layers) return lottie;

    const originalW = lottie.w ?? targetSize;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const layers = lottie.layers.map((layer: any) => {
        const scale = layer.ks?.s;
        if (!scale?.k?.[0]) return layer;

        const currentScale = scale.k[0];
        const multiplier = (TARGET_SCALE * targetSize) / (originalW * currentScale);

        return {
            ...layer,
            ks: {
                ...layer.ks,
                s: {
                    ...scale,
                    k: [currentScale * multiplier, currentScale * multiplier, scale.k[2] ?? 100],
                },
            },
        };
    });

    return { ...lottie, w: targetSize, h: targetSize, layers };
};

export const useAnimatedIcon = ({
    jsonAnimationData,
    initialDelay,
    repeatDelay,
    size = ICONSAX_SIZE.LARGE,
    autoplay = false,
    loop = false,
    colorInherit = false,
}: UseAnimatedIconOptions) => {
    const isHoveredRef = useRef(false);
    const initialTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const repeatTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onCompleteRef = useRef<() => void>();

    const normalizedAnimationData = useMemo(
        () => (jsonAnimationData ? normalizeLottieIcon(JSON.parse(JSON.stringify(jsonAnimationData)), size) : undefined),
        [jsonAnimationData, size],
    );

    const { View, animationItem } = useLottie({
        animationData: normalizedAnimationData,
        autoplay,
        loop,
        style: { height: size, width: size },
        onComplete: () => onCompleteRef.current?.(),
    });

    onCompleteRef.current = () => {
        if (!animationItem) return;
        animationItem.goToAndStop(0, true);

        if (isHoveredRef.current) {
            repeatTimerRef.current = setTimeout(() => {
                if (isHoveredRef.current) {
                    animationItem.goToAndPlay(0, true);
                }
            }, repeatDelay);
        }
    };

    const clearTimers = () => {
        if (initialTimerRef.current) {
            clearTimeout(initialTimerRef.current);
            initialTimerRef.current = null;
        }
        if (repeatTimerRef.current) {
            clearTimeout(repeatTimerRef.current);
            repeatTimerRef.current = null;
        }
    };

    const handleMouseEnter = () => {
        if (isHoveredRef.current) return;
        isHoveredRef.current = true;
        clearTimers();
        initialTimerRef.current = setTimeout(() => {
            if (isHoveredRef.current && animationItem) {
                animationItem.goToAndPlay(0, true);
            }
        }, initialDelay);
    };

    const handleMouseLeave = () => {
        isHoveredRef.current = false;
        clearTimers();
    };

    useEffect(() => {
        return () => clearTimers();
    }, []);

    const AnimatedIcon = colorInherit ? <ColorInheritWrapper>{View}</ColorInheritWrapper> : View;

    return { AnimatedIcon, handleMouseEnter, handleMouseLeave };
};
